// =============================================================================
// src/chrome/mini-toast/index.ts — host de toasts efemeros (.u-mini-toast).
//
// Proveniencia: porte FIEL de assets/unified-app.js `var MiniToast` (2751-2765).
//   Toast empilhavel no host #miniToastHost (markup em src/chrome/static-shell).
//   Timings sao CONSTRAINT de paridade do usuario: visivel 2200ms, depois ganha
//   `.is-leaving` e e removido 220ms apos (transicao CSS de saida). NAO alterar.
//
// DISTINCAO DELIBERADA (nao e duplicacao da regra DRY): este MiniToast NAO e o
//   `.action-toast` de src/app/commands.ts (aquele e o feedback bottom-right do
//   sendPrompt, com texto "→ ..." e timer de 2800ms). Este aqui e o host com
//   icone Tabler que o TopbarNav/SearchPalette usam para acoes utilitarias
//   (bookmark/download/filtro/foco). Sao dois componentes legados distintos.
//
// SRP: so cria/empilha/remove o no de toast. Sem dados, sem navegacao.
// =============================================================================

import type { AppContext } from '../../app/types';
import { byId } from '../../app/dom';

/** Id do host de toasts no shell (#miniToastHost em static-shell). */
const MINI_TOAST_HOST_ID = 'miniToastHost';
/** Classe base do toast (assets/unified.css). */
const MINI_TOAST_CLASS = 'u-mini-toast';
/** Classe aplicada para disparar a animacao de saida. */
const MINI_TOAST_LEAVING_CLASS = 'is-leaving';
/** Ms visivel antes de comecar a sair (legado: 2200). CONSTRAINT de paridade. */
const MINI_TOAST_VISIBLE_MS = 2200;
/** Ms da animacao de saida antes de remover do DOM (legado: 220). CONSTRAINT. */
const MINI_TOAST_LEAVE_MS = 220;

/**
 * Exibe um toast no host #miniToastHost. Reproduz `MiniToast.show` legado:
 * cria o no, opcionalmente prefixa um icone Tabler, anexa ao host e agenda a
 * saida (2200ms) + remocao (mais 220ms).
 *
 * @param message Texto curto exibido (ja confiavel; conteudo interno do app).
 * @param icon Classe de icone Tabler opcional (ex.: 'ti-bookmark-filled').
 * @example
 *   showMiniToast('Vista salva nos favoritos', 'ti-bookmark-filled');
 */
export function showMiniToast(message: string, icon?: string): void {
  const host = byId(MINI_TOAST_HOST_ID);
  const toastEl = buildMiniToastNode(message, icon);
  host.appendChild(toastEl);
  scheduleMiniToastDismissal(toastEl);
}

/** Monta o no do toast com a mesma estrutura do legado (icone + span). */
function buildMiniToastNode(message: string, icon?: string): HTMLDivElement {
  const toastEl = document.createElement('div');
  toastEl.className = MINI_TOAST_CLASS;
  const iconHtml = icon ? `<i class="ti ${icon}"></i>` : '';
  toastEl.innerHTML = `${iconHtml}<span>${message}</span>`;
  return toastEl;
}

/** Agenda saida (2200ms) e remocao do DOM (mais 220ms), igual ao legado. */
function scheduleMiniToastDismissal(toastEl: HTMLDivElement): void {
  setTimeout(() => {
    toastEl.classList.add(MINI_TOAST_LEAVING_CLASS);
    setTimeout(() => toastEl.remove(), MINI_TOAST_LEAVE_MS);
  }, MINI_TOAST_VISIBLE_MS);
}

/**
 * AppModule do host de toasts. O markup ja vem do shell (static-shell), entao
 * `render()` devolve string vazia e `init()` so valida que o host existe (falha
 * cedo com id ofensivo se o shell mudar). `showMiniToast` e a superficie real.
 */
export const MiniToastHost = {
  id: 'mini-toast',
  render(): string {
    return '';
  },
  // ctx faz parte do contrato AppModule mas o host nao depende dele (markup vem do shell).
  init(_ctx: AppContext): void {
    byId(MINI_TOAST_HOST_ID);
  },
};
